import * as THREE from 'three';
import { tuning } from '@/config/tuning';
import { CHEER_PROP_COLORS, PANTS_COLORS, SHIRT_COLORS, SKIN_TONES } from './palettes';
import type { PersonInst, ZoneCtx } from './types';

/**
 * scenery/crowd.ts — the one helper every zone uses to drop people (M36) and,
 * since GS3, the crowd MESH builder too (moved up from builders.ts so the
 * whole crowd system lives in one file).
 *
 * scatterPeople draws ONLY from `ctx.peopleRng` (a separate seeded stream), so
 * adding, tuning or removing crowds never shifts the main scenery rng sequence
 * — every existing building/tree/prop stays exactly where it was. Call it at
 * the END of a zone builder; nothing after it depends on its random draws.
 *
 * People gather in clusters (more natural than an even sprinkle): at each
 * placement slot there's a `chance` that a cluster of ~`count` people appears in
 * the lateral `band`. `tuning.scenery.crowdDensity` scales how often clusters
 * appear, so the whole world thins out (or empties) from one knob.
 */
export interface CrowdOpts {
  /** Lateral band [from, to] measured from the road CENTRE (use hw + offset). */
  band: [number, number];
  /** Probability a cluster appears at this step/side (before density scaling). */
  chance: number;
  /** Rough people per cluster (jittered ±40%). */
  count: number;
  /** Fraction seated rather than standing (0..1). Beaches/sea walls/lawns. */
  sit?: number;
  /** Per-person scale range (default 0.92..1.1). */
  scale?: [number, number];
  /** Fraction of the STANDING people who celebrate (both arms up, some with a
   *  scarf overhead). Finish plazas only — every other crowd leaves it 0. */
  cheer?: number;
}

export function scatterPeople(ctx: Pick<ZoneCtx, 'peopleRng' | 'at' | 'a'>, o: CrowdOpts): void {
  const density = tuning.scenery.crowdDensity;
  if (density <= 0) return;
  const r = ctx.peopleRng;
  if (r.next() > o.chance * density) return;
  const n = Math.max(1, Math.round(o.count * (0.6 + r.next() * 0.8)));
  const [s0, s1] = o.scale ?? [0.92, 1.1];
  const sit = o.sit ?? 0;
  const cheer = o.cheer ?? 0;
  for (let i = 0; i < n; i++) {
    const [x, y, z] = ctx.at(r.range(o.band[0], o.band[1]));
    const seated = r.next() < sit;
    ctx.a.people.push({
      x,
      y,
      z,
      rotY: r.range(0, Math.PI * 2),
      s: r.range(s0, s1),
      shirt: r.pick(SHIRT_COLORS),
      skin: r.pick(SKIN_TONES),
      pants: r.pick(PANTS_COLORS),
      sit: seated,
      // SHORT-CIRCUITED ON PURPOSE: at cheer 0 (every existing caller) this
      // takes NO rng draw, so the peopleRng sequence — and therefore every
      // shipped crowd in the game — is byte-identical to before the option
      // existed. Only a caller that opts in pays a draw.
      cheer: cheer > 0 && !seated && r.next() < cheer,
    });
  }
}

// ---- The crowd meshes (GS3 upgrade of the M36 builder) ----------------------
//
// SIX InstancedMeshes — torso (shirt), head (skin), two legs (pants), HEADWEAR
// (hair mops + the odd bright cap — bare coloured spheres read as LEGO pegs,
// measured at the finish plazas), ARMS (two slots per person) and, since the
// FINISH FANFARE round, SCARVES held overhead. Shirt saturation is pulled down
// 20% so the crowd reads as fabric next to the upgraded steel/gold plaza.
//
// THE FANFARE ADDITION — `PersonInst.cheer`. GS3 already gave ~26% of everyone
// ONE raised arm, which reads as "waving" but not as "celebrating"; the owner's
// note after playing all nine courses was that the finishes have fans SITTING,
// not fans CELEBRATING. A cheerer stands with BOTH arms up, rides a small
// hash-derived BOUNCE (so a packed rail line has a ragged head-height silhouette
// instead of a hedge) and ~44% hold a scarf between their hands. Measured: the
// scarf is what actually carries at racing speed — arms are 0.09 u thick and
// vanish past ~40 u, a 0.66 u scarf in a hot unshaded colour does not.
//
// COST: exactly ONE new draw call for the WHOLE GAME (the scarf mesh, and only
// when some crowd somewhere cheers). Arms doubled their INSTANCE count, which
// costs matrices, not draws — and the unused slot is zero-scaled, same as the
// GS3 skip convention.
//
// ALL per-person variety here derives from the INSTANCE INDEX via hash() —
// never from an rng — so positions and every other stream stay byte-identical
// (the M36 tree-variety rule).

/** Cheap deterministic 0..1 hash from the instance index. */
function hash(n: number): number {
  const s = Math.sin(n * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

const PERSON_TORSO = new THREE.CylinderGeometry(0.22, 0.15, 0.8, 6); // shoulders → waist
const PERSON_HEAD = new THREE.IcosahedronGeometry(0.18, 0);
const PERSON_LEG = new THREE.BoxGeometry(0.13, 0.95, 0.16); // long legs = human proportions
const PERSON_WEAR = new THREE.IcosahedronGeometry(0.19, 0); // hair mop / cap (squashed per instance)
const PERSON_ARM = new THREE.BoxGeometry(0.12, 0.58, 0.13); // the raised cheering arm
const PERSON_SCARF = new THREE.BoxGeometry(0.66, 0.26, 0.045); // held overhead, hand to hand

/** Hair tones (most heads) and cap colours (the few). */
const HAIR_COLORS = [0x17120e, 0x2c1e12, 0x4a3018, 0x6a4a24, 0x8a7a66, 0xd8c087, 0x3a3a42];
const CAP_COLORS = [0xc23a2e, 0x2a6ac0, 0xe8b23a, 0x2a8a4a, 0xe8e4da, 0x18191d];

/** Desaturate a shirt colour 20% (GS3 — full-sat shirts read as plastic). */
function desatShirt(color: THREE.Color, hex: number): THREE.Color {
  color.set(hex);
  const hsl = { h: 0, s: 0, l: 0 };
  color.getHSL(hsl);
  return color.setHSL(hsl.h, hsl.s * 0.8, hsl.l);
}

/** A crowd of bold low-poly figures. `sit` gives a seated silhouette (beaches,
 *  sea walls, grandstand tiers). Parts are oriented with the project heading
 *  convention (forward = sin/cos h) so seated legs reach forward correctly.
 *  Like all scenery, they cast no shadow. */
export function buildCrowd(insts: PersonInst[]): THREE.InstancedMesh[] {
  const torsoMat = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.85 });
  const headMat = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.7 });
  const legMat = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.9 });
  const wearMat = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.92 });
  const armMat = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.85 });
  const scarfMat = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.72 });
  // ACCESSORY MESHES ARE SIZED TO WHAT IS ACTUALLY WORN, not to the crowd.
  //
  // MEASURED, and it is the single most expensive thing in this file: an
  // InstancedMesh costs geometry-triangles × COUNT on the GPU whether or not an
  // instance is zero-scaled. GS3 allocated the headwear mesh at one slot per
  // person and zero-scaled the ~26% bare heads, which quietly paid 20 triangles
  // each for nothing; the first cut of the fanfare round then doubled the arm
  // slots for everyone and added a scarf slot for everyone, which cost NYC and
  // Tokyo ~23,000 and ~22,000 triangles for a pose only the finish crowd uses.
  //
  // Every pose flag here is a pure function of the instance index, so a cheap
  // pre-pass counts the slots exactly. Net effect: a city with a cheering finish
  // crowd now renders FEWER crowd triangles than it did before this round.
  let wearN = 0;
  let armN = 0;
  let scarfN = 0;
  for (let i = 0; i < insts.length; i++) {
    const cheering = insts[i].cheer === true && !insts[i].sit;
    if (hash(i * 3 + 1) >= 0.26) wearN++;
    armN += cheering ? 2 : hash(i * 5 + 2) < 0.26 ? 1 : 0;
    if (cheering && hash(i * 19 + 11) < 0.44) scarfN++;
  }
  const torsos = new THREE.InstancedMesh(PERSON_TORSO, torsoMat, insts.length);
  const heads = new THREE.InstancedMesh(PERSON_HEAD, headMat, insts.length);
  const legs = new THREE.InstancedMesh(PERSON_LEG, legMat, insts.length * 2);
  const wear = new THREE.InstancedMesh(PERSON_WEAR, wearMat, wearN);
  const arms = new THREE.InstancedMesh(PERSON_ARM, armMat, armN);
  const scarves = new THREE.InstancedMesh(PERSON_SCARF, scarfMat, scarfN);
  let wearI = 0;
  let armI = 0;
  let scarfI = 0;

  const mat = new THREE.Matrix4();
  const pos = new THREE.Vector3();
  const scl = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const qtilt = new THREE.Quaternion();
  const qleg = new THREE.Quaternion();
  const eul = new THREE.Euler();
  const col = new THREE.Color();

  insts.forEach((p, i) => {
    const s = p.s;
    const sinh = Math.sin(p.rotY);
    const cosh = Math.cos(p.rotY);
    scl.set(s, s, s);
    eul.set(0, p.rotY, 0);
    quat.setFromEuler(eul); // upright body yaw
    // FANFARE: a cheerer rides a frozen BOUNCE (0 … 0.22 u of scale, hash by
    // index) so a packed rail line has a ragged head-height silhouette rather
    // than a hedge. The whole figure lifts — feet included: at up to 0.2 u the
    // gap is invisible past a few metres, it reads as mid-jump, and the plaza's
    // crowd barrier stands 1.1 u tall in front of every leg anyway.
    const cheering = p.cheer === true && !p.sit;
    const bounce = cheering ? hash(i * 17 + 9) * 0.22 : 0;
    // local (right, up, forward) → world, scaled, on the person's spot
    const place = (lx: number, ly: number, lz: number): THREE.Vector3 =>
      pos.set(
        p.x + (cosh * lx + sinh * lz) * s,
        p.y + (ly + bounce) * s,
        p.z + (-sinh * lx + cosh * lz) * s,
      );

    const headY = p.sit ? 1.18 : 1.91;
    const headZ = p.sit ? 0.05 : 0;
    if (!p.sit) {
      for (const side of [-1, 1] as const) {
        const li = i * 2 + (side < 0 ? 0 : 1);
        legs.setMatrixAt(li, mat.compose(place(0.1 * side, 0.475, 0), quat, scl));
        legs.setColorAt(li, col.set(p.pants));
      }
      torsos.setMatrixAt(i, mat.compose(place(0, 1.35, 0), quat, scl));
      torsos.setColorAt(i, desatShirt(col, p.shirt));
      heads.setMatrixAt(i, mat.compose(place(0, 1.91, 0), quat, scl));
      heads.setColorAt(i, col.set(p.skin));
    } else {
      eul.set(1.35, 0, 0);
      qtilt.setFromEuler(eul); // tilt the leg box so its length reaches forward
      qleg.multiplyQuaternions(quat, qtilt); // …then swing it into the facing
      for (const side of [-1, 1] as const) {
        const li = i * 2 + (side < 0 ? 0 : 1);
        legs.setMatrixAt(li, mat.compose(place(0.1 * side, 0.22, 0.42), qleg, scl));
        legs.setColorAt(li, col.set(p.pants));
      }
      torsos.setMatrixAt(i, mat.compose(place(0, 0.66, 0.05), quat, scl));
      torsos.setColorAt(i, desatShirt(col, p.shirt));
      heads.setMatrixAt(i, mat.compose(place(0, 1.18, 0.05), quat, scl));
      heads.setColorAt(i, col.set(p.skin));
    }

    // HEADWEAR — hash-picked: ~26% bare, ~56% hair mop, ~18% bright cap. The
    // bare heads consume NO instance slot now (see the sizing note above).
    const wroll = hash(i * 3 + 1);
    if (wroll >= 0.26) {
      const wi = wearI++;
      if (wroll < 0.82) {
        // hair: a squashed shell hugging the top of the head
        scl.set(s * 1.04, s * 0.62, s * 1.06);
        wear.setMatrixAt(wi, mat.compose(place(0, headY + 0.06, headZ - 0.015), quat, scl));
        wear.setColorAt(wi, col.set(HAIR_COLORS[(hash(i * 7 + 3) * HAIR_COLORS.length) | 0]));
      } else {
        // cap: fuller, sits a touch higher, saturated colour
        scl.set(s * 1.14, s * 0.5, s * 1.16);
        wear.setMatrixAt(wi, mat.compose(place(0, headY + 0.1, headZ + 0.01), quat, scl));
        wear.setColorAt(wi, col.set(CAP_COLORS[(hash(i * 11 + 5) * CAP_COLORS.length) | 0]));
      }
      scl.set(s, s, s);
    }

    // ARMS. A CHEERER throws BOTH up (near-vertical, splayed a little); everyone
    // else keeps the GS3 single ~26% wave. Slots are packed, not per-person.
    const shoulderY = p.sit ? 0.94 : 1.62;
    const arm = (armSide: -1 | 1, roll: number, lift: number): void => {
      const ai = armI++;
      eul.set(0, p.rotY, armSide * roll);
      qtilt.setFromEuler(eul);
      arms.setMatrixAt(ai, mat.compose(place(0.3 * armSide, shoulderY + lift, headZ), qtilt, scl));
      arms.setColorAt(ai, desatShirt(col, p.shirt)); // sleeved arm = shirt
    };
    if (cheering) {
      // 0.20 rad of splay, hands ~0.42 above the shoulder: read as "both arms
      // UP". A wider roll (the 0.42 the one-arm wave uses) reads as a shrug
      // once both sides do it at once.
      arm(-1, 0.2, 0.42);
      arm(1, 0.2, 0.42);
    } else if (hash(i * 5 + 2) < 0.26) {
      arm(hash(i * 13 + 7) < 0.5 ? -1 : 1, 0.42, 0.26);
    }

    // SCARF — ~44% of cheerers hold one overhead between their hands. This is
    // the element that actually reads at racing speed (see the header note):
    // hot, unshaded colour, 0.66 u wide, held above the head line.
    const sroll = hash(i * 19 + 11);
    if (cheering && sroll < 0.44) {
      const si = scarfI++;
      const w = 0.86 + sroll * 0.9; // 0.86 … 1.25 of the base width
      scl.set(s * w, s, s);
      eul.set(0, p.rotY, (hash(i * 23 + 13) - 0.5) * 0.5); // a little cloth tilt
      qtilt.setFromEuler(eul);
      scarves.setMatrixAt(si, mat.compose(place(0, shoulderY + 0.74, headZ + 0.16), qtilt, scl));
      scarves.setColorAt(si, col.set(CHEER_PROP_COLORS[(hash(i * 29 + 17) * CHEER_PROP_COLORS.length) | 0]));
      scl.set(s, s, s);
    }
  });
  for (const m of [torsos, heads, legs, wear, arms, scarves]) {
    m.instanceMatrix.needsUpdate = true;
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
  }
  return [torsos, heads, legs, wear, arms, scarves];
}
